'use client';
import { useQuery } from '@apollo/client/react';

import { startOfMinute } from 'date-fns';

import { use } from 'react';

import { usePusherHandler } from '@/hooks/usePusherHandler';

import { nonFalsey } from 'Utils/nonFalsey';

import { useIsFirstRender } from '@/hooks/useIsFirstRender';
import { graphql } from '@/gql';

import { CurrentUserContext } from '@/context/CurrentUserContext';

import type { GenericEventsCollectionFilters } from './GenericEventsCollection';
import { GenericEventsCollection } from './GenericEventsCollection';

interface Props {
  className?: string | undefined;
  groupIds: string[] | null;
  title: React.ReactNode;
  emptyTitle: React.ReactNode;
  from: Date;
  to: Date;
  includeOngoing: boolean;
  filters?: GenericEventsCollectionFilters | undefined;
}

export const MULTI_GROUP_EVENTS_QUERY = graphql(`
  query MultiGroupEventsQuery(
    $userId: ID!
    $groupIds: [ID]
    $eventStart: DateTime!
    $eventEnd: DateTime!
    $includeOngoing: Boolean!
    $first: Int
  ) {
    user(id: $userId) {
      _id
      genericEventsInTimeRangeConnection(
        start: $eventStart
        end: $eventEnd
        filterBy: {
          groupIds: $groupIds
          includeOngoing: $includeOngoing
          includeGovernanceVisits: true
        }
        first: $first
      ) {
        edges {
          node {
            ... on Event {
              ...genericEventEvent
            }
            ... on UserGenericEventTrainingSession {
              ...genericEventTraining
            }
            ... on GovernanceVisit {
              ...genericEventGovernanceVisitFields
            }
          }
        }
      }
    }
  }
`);

export const MultiGroupEventsCollection = ({
  className,
  groupIds,
  title,
  emptyTitle,
  from,
  to,
  includeOngoing,
  filters = { limit: 5, reverse: false },
}: Props) => {
  const isFirstRender = useIsFirstRender();
  const { currentUserId } = use(CurrentUserContext);

  const { data, loading, refetch } = useQuery(MULTI_GROUP_EVENTS_QUERY, {
    variables: {
      userId: currentUserId || '',
      groupIds,
      eventStart: startOfMinute(from).toISOString(),
      eventEnd: startOfMinute(to).toISOString(),
      includeOngoing,
      first: filters?.limit ?? null,
    },
    skip: !currentUserId,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'none',
  });

  // Prevents the component from showing no upcoming events on the first render
  const isLoading = loading || isFirstRender;

  usePusherHandler(
    groupIds && groupIds.length ? groupIds.map((id) => `object-${id}`) : null,
    'child-updated',
    refetch,
  );

  const user = data?.user;

  const events =
    user?.genericEventsInTimeRangeConnection?.edges
      .map((edge) => {
        return edge?.node ?? null;
      })
      .filter(nonFalsey) || [];

  return (
    <GenericEventsCollection
      className={className}
      events={events}
      title={title}
      emptyTitle={emptyTitle}
      loading={isLoading}
      showOwner={true}
      filters={filters}
    />
  );
};
